import { makeStyles } from "tss-react/mui"
import { Box, Button, Grid, Link, Skeleton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Link as RouterLink } from "react-router-dom"
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { AgreementStatus, AgreementStatusVariant } from "../../../settings/posAcquirer/SupplierAdminPOSTypes"
import { AcquiringMerchantDetails, BankAccountStatus, BankAccountDetails } from "../supplierMerchantsTypes"
import { SUPPLIER_ADMIN_ACQUIRING_ROUTE } from "../../../../../../constants/routes"

type ContentBasedOnStatusAndDetailsProps = {
    status: AgreementStatusVariant | undefined
    acquiringDetails?: AcquiringMerchantDetails
    bankAccountDetails: BankAccountDetails
    isBankAccount: boolean
    isLoading?: boolean
    readonly?: boolean
    handleClick: () => void
}

const useStyles = makeStyles()((theme) => ({
    infoBox: {
        display: "grid",
        gridTemplateColumns: "0.875rem 1fr",
        gap: "0.5rem",
        fontSize: "0.875rem",
        color: theme.palette.tailwind.slate[600],
        "& >svg": {
            marginTop: "0.125rem",
        },
    },
    actionButton: {
        textTransform: "capitalize",
        color: theme.palette.tailwind.sky[500],
        borderRadius: "0.5rem",
    },
}))

export function CreditCardContentBasedOnStatusAndDetails({
    status,
    acquiringDetails,
    bankAccountDetails,
    isBankAccount,
    handleClick,
    isLoading = false,
    readonly = false,
}: ContentBasedOnStatusAndDetailsProps) {
    const { classes } = useStyles()

    if (isLoading) {
        return (
            <Stack width="100%" direction="column" spacing=".25rem">
                <Skeleton width="50%" height="2.5rem" />
                <Skeleton width="80%" height="3rem" />
            </Stack>
        )
    }

    if (!isBankAccount) {
        return (
            <>
                <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-no-registered-bank-account">
                    <StandardChip variant="yellow">No registered bank account</StandardChip>
                </LocalizedStrict>
                <Box className={classes.infoBox}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict
                        id="create-or-edit-supplier-admin-merchant-status-no-registered-bank-account-description"
                        elems={{
                            retryLink: <Link component={RouterLink} to={SUPPLIER_ADMIN_ACQUIRING_ROUTE}></Link>,
                        }}
                    >
                        <Typography variant="body2">
                            Register a bank account for your acquiring agreement <Link>here</Link>
                        </Typography>
                    </LocalizedStrict>
                </Box>
                {!readonly && (
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-setup">
                        <Button
                            onClick={handleClick}
                            variant="outlined"
                            size="small"
                            className={classes.actionButton}
                            disabled
                        >
                            Setup
                        </Button>
                    </LocalizedStrict>
                )}
            </>
        )
    } else if (status === AgreementStatus.VALID) {
        if (acquiringDetails?.acquiring && bankAccountDetails?.status === BankAccountStatus.VALID) {
            return (
                <>
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-approved">
                        <StandardChip variant="success">Active</StandardChip>
                    </LocalizedStrict>
                    <Grid>
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-acquirer">
                            <Typography variant="subtitle2">Acquirer:</Typography>
                        </LocalizedStrict>
                        <LocalizedEnum
                            base="create-or-edit-supplier-admin-merchant-acquirer-type"
                            enumValue={acquiringDetails?.acquiring.type}
                        >
                            <Typography variant="body2">{acquiringDetails?.acquiring.type}</Typography>
                        </LocalizedEnum>
                    </Grid>
                    <Grid>
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-bank-details">
                            <Typography variant="subtitle2">Bank details:</Typography>
                        </LocalizedStrict>
                        <Typography variant="body2">{bankAccountDetails?.bankName}</Typography>
                        <Typography variant="body2">{bankAccountDetails.accountIdentifier}</Typography>
                    </Grid>
                    {!readonly && (
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-edit">
                            <Button
                                onClick={handleClick}
                                variant="outlined"
                                size="small"
                                className={classes.actionButton}
                            >
                                Edit
                            </Button>
                        </LocalizedStrict>
                    )}
                </>
            )
        } else if (acquiringDetails?.acquiring && !!acquiringDetails?.acquiring.type) {
            return (
                <>
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-bank-account-pending">
                        <StandardChip variant="yellow">Bank account pending</StandardChip>
                    </LocalizedStrict>
                    <Box className={classes.infoBox}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-bank-account-pending-description">
                            <Typography variant="body2">
                                Wait for acquirer to finish verifying your bank account.
                            </Typography>
                        </LocalizedStrict>
                    </Box>
                    <Grid>
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-acquirer">
                            <Typography variant="subtitle2">Acquirer:</Typography>
                        </LocalizedStrict>
                        <LocalizedEnum
                            base="create-or-edit-supplier-admin-merchant-acquirer-type"
                            enumValue={acquiringDetails?.acquiring.type}
                        >
                            <Typography variant="body2">{acquiringDetails?.acquiring.type}</Typography>
                        </LocalizedEnum>
                    </Grid>
                    <Grid>
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-bank-details">
                            <Typography variant="subtitle2">Bank details:</Typography>
                        </LocalizedStrict>
                        <Typography variant="body2">{bankAccountDetails?.bankName}</Typography>
                        <Typography variant="body2">{bankAccountDetails?.accountIdentifier}</Typography>
                    </Grid>
                    {!readonly && (
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-edit">
                            <Button
                                onClick={handleClick}
                                variant="outlined"
                                size="small"
                                className={classes.actionButton}
                            >
                                Edit
                            </Button>
                        </LocalizedStrict>
                    )}
                </>
            )
        } else
            return (
                <>
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-no-payment-setup">
                        <StandardChip variant="yellow">No payment setup</StandardChip>
                    </LocalizedStrict>
                    <Box className={classes.infoBox}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-no-payment-setup-description">
                            <Typography variant="body1">
                                Setup credit card payments for this merchant to begin receiving payments.
                            </Typography>
                        </LocalizedStrict>
                    </Box>
                    {!readonly && (
                        <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-setup">
                            <Button
                                onClick={handleClick}
                                variant="outlined"
                                size="small"
                                className={classes.actionButton}
                            >
                                Setup
                            </Button>
                        </LocalizedStrict>
                    )}
                </>
            )
    } else if (status === AgreementStatus.PENDING) {
        return (
            <>
                <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-pending">
                    <StandardChip variant="yellow">Agreement pending</StandardChip>
                </LocalizedStrict>
                <Box className={classes.infoBox}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-pending-description">
                        <Typography variant="body2">
                            Wait for acquirer to finish processing your acquiring agreement application.
                        </Typography>
                    </LocalizedStrict>
                </Box>
                {!readonly && (
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-setup">
                        <Button
                            onClick={handleClick}
                            variant="outlined"
                            size="small"
                            className={classes.actionButton}
                            disabled
                        >
                            Setup
                        </Button>
                    </LocalizedStrict>
                )}
            </>
        )
    } else {
        return (
            <>
                <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-invalid">
                    <StandardChip variant="yellow">Agreement invalid</StandardChip>
                </LocalizedStrict>
                <Box className={classes.infoBox}>
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <LocalizedStrict
                        id="create-or-edit-supplier-admin-merchant-status-invalid-description"
                        elems={{
                            retryLink: <Link component={RouterLink} to={SUPPLIER_ADMIN_ACQUIRING_ROUTE}></Link>,
                        }}
                    >
                        <Typography variant="body2">
                            Correct your application for acquiring agreement <Link>here</Link>
                        </Typography>
                    </LocalizedStrict>
                </Box>
                {!readonly && (
                    <LocalizedStrict id="create-or-edit-supplier-admin-merchant-status-action-button-setup">
                        <Button onClick={handleClick} variant="outlined" size="small" className={classes.actionButton}>
                            Setup
                        </Button>
                    </LocalizedStrict>
                )}
            </>
        )
    }
}
